import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const technologies = [
  // Frontend
  { name: 'React', slug: 'react', color: '#61DAFB', category: 'Frontend', desc: 'Modern UI library' },
  { name: 'Next.js', slug: 'nextdotjs', color: '#000000', category: 'Frontend', desc: 'React meta-framework' },
  { name: 'Vue.js', slug: 'vuedotjs', color: '#4FC08D', category: 'Frontend', desc: 'Progressive JS framework' },
  { name: 'Angular', slug: 'angular', color: '#DD0031', category: 'Frontend', desc: 'Enterprise web framework' },

  // Backend
  { name: 'Node.js', slug: 'nodedotjs', color: '#339933', category: 'Backend', desc: 'Server-side JS runtime' },
  { name: 'Python', slug: 'python', color: '#3776AB', category: 'Backend', desc: 'Data & backend language' },
  { name: 'Java', slug: 'java', color: '#ED8B00', category: 'Backend', desc: 'Robust enterprise platform' },
  { name: 'PHP', slug: 'php', color: '#777BB4', category: 'Backend', desc: 'Web scripting language' },
  { name: 'ASP.NET', slug: 'dotnet', color: '#512BD4', category: 'Backend', desc: 'Cross-platform app framework' },

  // Mobile
  { name: 'React Native', slug: 'react', color: '#61DAFB', category: 'Mobile', desc: 'Cross-platform native apps' },
  { name: 'Flutter', slug: 'flutter', color: '#02569B', category: 'Mobile', desc: 'Google UI toolkit' },
  { name: 'iOS', slug: 'apple', color: '#000000', category: 'Mobile', desc: 'Native Apple iOS' },
  { name: 'Android', slug: 'android', color: '#3DDC84', category: 'Mobile', desc: 'Native Android OS' },

  // Cloud & DevOps
  { name: 'AWS', slug: 'amazonwebservices', color: '#FF9900', category: 'Cloud & DevOps', desc: 'Amazon Web Services' },
  { name: 'Azure', slug: 'microsoftazure', color: '#0078D4', category: 'Cloud & DevOps', desc: 'Microsoft Cloud' },
  { name: 'Docker', slug: 'docker', color: '#2496ED', category: 'Cloud & DevOps', desc: 'Containerization platform' },
  { name: 'Google Cloud', slug: 'googlecloud', color: '#4285F4', category: 'Cloud & DevOps', desc: 'Google Cloud Platform' },
  { name: 'CI/CD', slug: 'githubactions', color: '#2088FF', category: 'Cloud & DevOps', desc: 'Continuous deployment' },

  // Automation / No-Code
  { name: 'Zapier', slug: 'zapier', color: '#FF4A00', category: 'Automation', desc: 'Workflow integration tool' },
  { name: 'n8n', slug: 'n8n', color: '#FF6D5A', category: 'Automation', desc: 'Fair-code workflow tool' },
  { name: 'Make.com', slug: 'make', color: '#EA247E', category: 'Automation', desc: 'Visual flow automation' },
];

const categories = ['All', 'Frontend', 'Backend', 'Mobile', 'Cloud & DevOps', 'Automation'];

const Technologies = () => {
  const [activeCategory, setActiveCategory] = useState('All');
  const [hoveredIndex, setHoveredIndex] = useState(null);

  const filteredTech = activeCategory === 'All'
    ? technologies
    : technologies.filter(tech => tech.category === activeCategory);

  return (
    <section id="technologies" className="py-24 bg-transparent  backdrop-blur-md relative border-t border-slate-200/50 overflow-hidden">
      {/* Decorative Background Elements */}
      <div className="absolute top-1/4 left-1/10 w-96 h-96 bg-blue-100/30 rounded-full blur-3xl -z-10" />
      <div className="absolute bottom-1/4 right-1/10 w-96 h-96 bg-indigo-100/30 rounded-full blur-3xl -z-10" />

      <div className="container mx-auto px-6 max-w-7xl relative z-10">
        
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <motion.span 
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-xs uppercase tracking-widest font-extrabold text-blue-600 bg-blue-50 px-4 py-1.5 rounded-full inline-block mb-4"
          >
            Our Tech Stack
          </motion.span>
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl font-black text-slate-900 mb-6 tracking-tight"
          >
            Technologies We Work With
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-lg text-slate-600"
          >
            We leverage modern, secure, and industry-proven technologies to build scalable software solutions that stand the test of time and accelerate your business growth.
          </motion.p>
        </div>

        {/* Category Filter Tabs */}
        <div className="flex justify-center mb-12">
          <div className="flex flex-wrap gap-2 p-1.5 bg-slate-100/80 backdrop-blur-sm rounded-2xl border border-slate-200/50 max-w-full overflow-x-auto justify-center">
            {categories.map((category) => {
              const isActive = activeCategory === category;
              return (
                <button
                  key={category}
                  onClick={() => setActiveCategory(category)}
                  className="relative px-5 py-2.5 rounded-xl text-sm font-semibold transition-colors duration-200 whitespace-nowrap focus:outline-none"
                  style={{
                    color: isActive ? '#fff' : '#64748b'
                  }}
                >
                  {isActive && (
                    <motion.div
                      layoutId="activeTabPill"
                      className="absolute inset-0 bg-blue-600 rounded-xl shadow-md"
                      transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                    />
                  )}
                  <span className="relative z-10">{category}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Tech Grid */}
        <motion.div 
          layout
          className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-6"
        >
          <AnimatePresence mode="popLayout">
            {filteredTech.map((tech) => {
              const isHovered = hoveredIndex === tech.name;
              return (
                <motion.div
                  layout
                  key={tech.name}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  whileHover={{ y: -6, scale: 1.03 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                  onMouseEnter={() => setHoveredIndex(tech.name)}
                  onMouseLeave={() => setHoveredIndex(null)}
                  className="relative group bg-white rounded-2xl p-6 flex flex-col items-center justify-center border transition-all duration-300 cursor-pointer"
                  style={{
                    borderColor: isHovered ? tech.color : '#f1f5f9',
                    boxShadow: isHovered 
                      ? `0 12px 25px -10px ${tech.color}45, 0 4px 12px -5px ${tech.color}25` 
                      : '0 4px 6px -1px rgba(15, 23, 42, 0.05), 0 2px 4px -2px rgba(15, 23, 42, 0.05)',
                  }}
                >
                  {/* Technology Logo */}
                  <div className="relative w-14 h-14 mb-4 flex items-center justify-center">
                    {/* Subtle Brand Color Radial Glow on Card Hover */}
                    <div 
                      className="absolute inset-0 rounded-full blur-md opacity-0 group-hover:opacity-20 transition-opacity duration-300"
                      style={{ backgroundColor: tech.color }}
                    />
                    <img
                      src={`https://cdn.simpleicons.org/${tech.slug}`}
                      alt={`${tech.name} logo`}
                      className="w-12 h-12 object-contain transition-all duration-300 filter grayscale group-hover:grayscale-0 relative z-10"
                      onError={(e) => {
                        // Fallback in case Simple Icons CDN is blocked or unavailable
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    {/* Fallback UI element if image fails to load */}
                    <div 
                      className="hidden w-12 h-12 rounded-full items-center justify-center font-bold text-white text-lg relative z-10"
                      style={{ backgroundColor: tech.color || '#3b82f6' }}
                    >
                      {tech.name.substring(0, 2)}
                    </div>
                  </div>

                  {/* Technology Info */}
                  <h3 className="text-sm font-bold text-slate-800 text-center tracking-tight transition-colors duration-200 group-hover:text-slate-900">
                    {tech.name}
                  </h3>
                  <span className="text-[11px] text-slate-400 text-center mt-1 font-medium leading-tight opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    {tech.desc}
                  </span>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </motion.div>

      </div>
    </section>
  );
};

export default Technologies;
